import PauseIcon from "@mui/icons-material/Pause"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Popover from "@mui/material/Popover"
import Slider from "@mui/material/Slider"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { Howl } from "howler"
import { useEffect, useRef, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"

export default function CallRecordingPlayer({ recordingFile }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playPositionSeconds, setPlayPositionSeconds] = useState(0)
  const [durationSeconds, setDurationSeconds] = useState(0)
  const [volumeSliderAnchorElem, setVolumeSliderAnchorElem] = useState(null)
  const DEFAULT_VOLUME = 0.5
  const [volume, setVolume] = useState(DEFAULT_VOLUME)
  const howlerInstance = useRef(null)

  function handlePlayPauseButtonClicked() {
    if (isPlaying) {
      howlerInstance.current.pause()
    } else {
      howlerInstance.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  function formattedProgress() {
    function pad2Zero(value) {
      return String(value).padStart(2, "0")
    }

    function secondsToMinutes(seconds) {
      return Math.floor(seconds / 60)
    }

    function secondsRemainder(seconds) {
      return pad2Zero(Math.round(seconds % 60))
    }

    if (!howlerInstance.current) {
      return
    }

    const totalMinutes = secondsToMinutes(durationSeconds)
    const totalSeconds = secondsRemainder(durationSeconds)
    const playedMinutes = secondsToMinutes(playPositionSeconds)
    const playedSeconds = secondsRemainder(playPositionSeconds)
    return `${playedMinutes}:${playedSeconds}/ ${totalMinutes}:${totalSeconds}`
  }

  function handlePositionSliderChanged(e, newValue) {
    setPlayPositionSeconds(newValue)
    howlerInstance.current?.seek(newValue)
  }

  function handleVolumeSliderChanged(e, newValue) {
    setVolume(newValue)
    howlerInstance.current?.volume(newValue)
  }

  useEffect(() => {
    if (!recordingFile) {
      return undefined
    }
    const howler = new Howl({
      src: recordingFile,
      volume: DEFAULT_VOLUME,
      onload: () => setDurationSeconds(howler.duration()),
      onend: () => {
        setIsPlaying(false)
        setPlayPositionSeconds(0)
      },
    })
    howlerInstance.current = howler
    return () => {
      howler.unload?.()
      howlerInstance.current = null
    }
  }, [recordingFile])

  useEffect(() => {
    let seekInterval
    if (isPlaying) {
      seekInterval = setInterval(
        () => setPlayPositionSeconds(howlerInstance.current.seek()),
        1000
      )
    } else {
      clearInterval(seekInterval)
    }
    return () => clearInterval(seekInterval)
  }, [isPlaying])

  if (!recordingFile) {
    return null
  }

  return (
    <Paper
      elevation={1}
      sx={{
        display: "flex",
        alignItems: "center",
        height: { xs: "38px", sm: "42px" },
        width: { xs: "100%", sm: "fit-content" },
        paddingInline: { xs: "8px", sm: "12px" },
        gap: 1,
      }}
    >
      <PlayPauseIconButton
        aria-label={isPlaying ? "Pause recording" : "Play recording"}
        onClick={handlePlayPauseButtonClicked}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </PlayPauseIconButton>
      <Row>
        <Typography variant="h6" fontSize="12px" sx={{ textWrap: "nowrap" }}>
          {formattedProgress()}
        </Typography>
      </Row>
      <Slider
        color="primary"
        value={playPositionSeconds}
        onChange={handlePositionSliderChanged}
        max={durationSeconds}
        step={1}
        sx={{
          width: { sm: "320px" },
          flexGrow: { xs: 1 },
          ml: 1,
          "& .MuiSlider-thumb": { display: "none" },
        }}
      />
      <IconButton
        size="small"
        aria-label="Recording volume"
        onClick={(e) => setVolumeSliderAnchorElem(e.currentTarget)}
      >
        <VolumeUpIcon variant="g600" sx={{ width: "10px", height: "10px" }} />
      </IconButton>
      <Popover
        open={Boolean(volumeSliderAnchorElem)}
        anchorEl={volumeSliderAnchorElem}
        onClose={() => setVolumeSliderAnchorElem(null)}
        anchorOrigin={{
          horizontal: "center",
          vertical: "top",
        }}
        transformOrigin={{
          horizontal: "center",
          vertical: 80,
        }}
        elevation={0}
      >
        <Column
          sx={{
            height: "80px",
            width: "16px",
            alignItems: "center",
            paddingBlock: "8px",
            overflowY: "clip",
          }}
        >
          <Slider
            value={volume}
            onChange={handleVolumeSliderChanged}
            step={0.1}
            max={1}
            orientation="vertical"
            size="small"
            sx={{
              heigt: "60px",
              "& .MuiSlider-thumb": {
                width: "8px",
                height: "8px",
              },
            }}
          />
        </Column>
      </Popover>
    </Paper>
  )
}

const PlayPauseIconButton = styled((props) => (
  <IconButton size="small" {...props} />
))(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    fill: theme.palette.colors.gray600,
    width: "16px",
    height: "16px",
  },
}))
